import type React from "react";
import { createHashRouter } from "react-router";
import { RootLayout } from "./components/layout/RootLayout";
import { PageLayout } from "./components/layout/PageLayout";
import { TemplateLayout } from "./components/layout/TemplateLayout";
import { NotFoundPage } from "./pages/NotFoundPage";
import { RouteFallback } from "./components/shared/RouteFallback";
import { DashboardPage } from "./pages/DashboardPage";

/* ================================================================
   Helper – shorthand for lazy-loaded named exports
   ================================================================ */
const lazy = (
  loader: () => Promise<Record<string, unknown>>,
  exportName?: string,
) => ({
  lazy: async () => {
    let mod: Record<string, unknown>;
    try {
      mod = await loader();
    } catch (err) {
      // Chunk no longer exists after a new deployment — force reload to get fresh HTML
      if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
        window.location.reload();
        return { Component: () => null };
      }
      throw err;
    }
    const key = exportName || Object.keys(mod).find(k => k !== "default" && typeof mod[k] === "function") || "default";
    return { Component: mod[key] as React.ComponentType };
  },
});

/* ================================================================
   Site children (inside TemplateLayout > PageLayout)
   ================================================================ */
const siteChildren = [
  // Dynamic home page (also handles facelift overview via TemplateTypeRouter)
  {
    index: true,
    ...lazy(() => import("./components/shared/TemplateTypeRouter"), "TemplateTypeRouter"),
  },

  // Shared pages (all template types)
  { path: "about", ...lazy(() => import("./components/shared/TemplateTypeRouter"), "AboutPageRouter") },
  { path: "blog", ...lazy(() => import("./pages/BlogPage"), "BlogPage") },
  { path: "blog/:slug", ...lazy(() => import("./pages/BlogPostPage"), "BlogPostPage") },
  { path: "contact", ...lazy(() => import("./pages/ContactPage"), "ContactPage") },
  { path: "seo-manager", ...lazy(() => import("./pages/SeoManagerPage"), "SeoManagerPage") },
  { path: "change-history", ...lazy(() => import("./pages/ChangeHistoryPage"), "ChangeHistoryPage") },
  { path: "activity-log", ...lazy(() => import("./pages/ActivityLogPage"), "ActivityLogPage") },
  { path: "business-info", ...lazy(() => import("./pages/BusinessInfoPage"), "BusinessInfoPage") },
  { path: "qa-checklist", ...lazy(() => import("./pages/QaChecklistPage"), "QaChecklistPage") },
  { path: "site-tree", ...lazy(() => import("./pages/SiteTreePage"), "SiteTreePage") },
  { path: "media", ...lazy(() => import("./pages/MediaLibraryPage"), "MediaLibraryPage") },

  // Facelift-specific: individual page detail
  { path: "fl-page/:pageId", ...lazy(() => import("./pages/FaceliftPageDetailPage"), "FaceliftPageDetailPage") },

  // Branding-only pages
  { path: "services", ...lazy(() => import("./pages/ServicesPage"), "ServicesPage") },
  { path: "service-a", ...lazy(() => import("./pages/ServiceAPage"), "ServiceAPage") },
  { path: "service-b", ...lazy(() => import("./pages/ServiceBPage"), "ServiceBPage") },
  { path: "products", ...lazy(() => import("./pages/ProductsPage"), "ProductsPage") },
  { path: "product-a", ...lazy(() => import("./pages/ProductAPage"), "ProductAPage") },
  { path: "product-b", ...lazy(() => import("./pages/ProductBPage"), "ProductBPage") },
  { path: "projects", ...lazy(() => import("./pages/ProjectsPage"), "ProjectsPage") },
  { path: "consulting", ...lazy(() => import("./pages/ConsultingPage"), "ConsultingPage") },

  // Store-only pages
  { path: "catalog", ...lazy(() => import("./pages/store/CatalogPage"), "CatalogPage") },
  { path: "catalog/:categorySlug", ...lazy(() => import("./pages/store/CategoryPage"), "CategoryPage") },
  { path: "catalog/:categorySlug/:productSlug", ...lazy(() => import("./pages/store/ProductPage"), "ProductPage") },
  { path: "cart", ...lazy(() => import("./pages/store/CartPage"), "CartPage") },
  { path: "checkout", ...lazy(() => import("./pages/store/CheckoutPage"), "CheckoutPage") },
  { path: "thank-you", ...lazy(() => import("./pages/store/ThankYouPage"), "ThankYouPage") },

  // Catch-all for custom slugs
  { path: ":customSlug", ...lazy(() => import("./components/shared/SlugResolver"), "SlugResolver") },
  { path: "*", Component: NotFoundPage },
];

/* ================================================================
   Top-level route config
   ================================================================ */
const routeConfig = [
  // Client intake form — standalone (outside admin layout, no auth required)
  {
    path: "/intake/:token",
    HydrateFallback: RouteFallback,
    ...lazy(() => import("./pages/ClientIntakeFormPage"), "ClientIntakeFormPage"),
  },
  // Client portal — self-service for clients
  {
    path: "/client-portal",
    HydrateFallback: RouteFallback,
    ...lazy(() => import("./pages/ClientPortalPage"), "ClientPortalPage"),
  },
  // Intake login — gate for the forms manager
  {
    path: "/intake-login",
    HydrateFallback: RouteFallback,
    ...lazy(() => import("./pages/IntakeLoginPage"), "IntakeLoginPage"),
  },
  // Intake manager — standalone page with own header (outside template)
  {
    path: "/intake-forms",
    HydrateFallback: RouteFallback,
    ...lazy(() => import("./pages/IntakeManagerPage"), "IntakeManagerPage"),
  },
  // Intake templates — standalone dashboard for managing marketplace templates
  {
    path: "/intake-templates",
    HydrateFallback: RouteFallback,
    ...lazy(() => import("./pages/IntakeTemplatesPage"), "IntakeTemplatesPage"),
  },
  {
    path: "/",
    Component: RootLayout,
    HydrateFallback: RouteFallback,
    children: [
      { index: true, Component: DashboardPage },
      { path: "profile", ...lazy(() => import("./pages/ProfilePage"), "ProfilePage") },
      { path: "settings", ...lazy(() => import("./pages/SystemSettingsPage"), "SystemSettingsPage") },
      { path: "smoke-test", ...lazy(() => import("./pages/SmokeTestPage"), "SmokeTestPage") },
      { path: "audit-log", ...lazy(() => import("./pages/AuditLogPage"), "AuditLogPage") },
      { path: "feature-flags", ...lazy(() => import("./pages/FeatureFlagsPage"), "FeatureFlagsPage") },
      { path: "dev-guide", ...lazy(() => import("./pages/DevGuidePage"), "DevGuidePage") },
      { path: "code-recipes", ...lazy(() => import("./pages/CodeRecipesPage"), "CodeRecipesPage") },
      { path: "vault", ...lazy(() => import("./pages/VaultHubPage"), "VaultHubPage") },
      { path: "media-library", ...lazy(() => import("./pages/MediaLibraryPage"), "MediaLibraryPage") },
      { path: "review-center", ...lazy(() => import("./pages/ReviewCenterPage"), "ReviewCenterPage") },
      { path: "automations", ...lazy(() => import("./pages/AutomationsPage"), "AutomationsPage") },
      { path: "calendar", ...lazy(() => import("./pages/DeadlineCalendarPage"), "DeadlineCalendarPage") },
      { path: "client-board/:templateId", ...lazy(() => import("./pages/ClientBoardPage"), "ClientBoardPage") },
      {
        path: "t/:templateId",
        Component: TemplateLayout,
        children: [
          {
            Component: PageLayout,
            children: siteChildren,
          },
        ],
      },
      { path: "site-tree-share/:token", ...lazy(() => import("./pages/SiteTreeSharedPage"), "SiteTreeSharedPage") },
      { path: "*", Component: NotFoundPage },
    ],
  },
];

/**
 * Lazy router factory — MUST be called AFTER any URL rewriting
 * (e.g. query-param → hash redirect in App.tsx) so that
 * createHashRouter reads the corrected window.location.hash.
 *
 * Previously `createHashRouter` was called at module top-level,
 * which meant it ran BEFORE the redirect and snapshot the wrong
 * initial location (empty hash → index route → login screen).
 */
export function createAppRouter() {
  return createHashRouter(routeConfig);
}